import React, { useState } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import classNames from "classnames";

import ComboBoxItem from "./ComboBoxItem";

import styles from "./ComboBox.module.scss";
import { Check, ChevronDownIcon } from "lucide-react";

interface ComboboxProps<T> {
  className?: string;
  placeholder?: string;
  selected: ComboBoxItem<T> | ComboBoxItem<T>[] | undefined;
  options: ComboBoxItem<T>[];
  onChange:
    | ((value: ComboBoxItem<T>) => void)
    | ((value: ComboBoxItem<T>[]) => void);
  lean?: boolean;
  unfilled?: boolean;
  multiple?: boolean;
  error?: boolean;
}

const ComboBox = <T,>({
  className = "",
  placeholder,
  selected,
  options,
  onChange,
  lean,
  unfilled,
  multiple = false,
  error = false,
}: ComboboxProps<T>) => {
  const [query, setQuery] = useState<string>("");

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase()),
        );

  return (
    <Combobox
      multiple={multiple}
      value={selected}
      onChange={onChange}
      onClose={() => setQuery("")}
    >
      <div className={styles.inputContainer}>
        <ComboboxInput
          className={classNames(
            styles.listbox,
            lean && styles.lean,
            unfilled && styles.unfilled,
            error && styles.listBoxError,
            className,
          )}
          placeholder={multiple ? "" : placeholder || "Select one..."}
          displayValue={
            !multiple
              ? (option?: ComboBoxItem<T>) => option?.label ?? ""
              : undefined
          }
          onChange={(event) => setQuery(event.target.value)}
        />
        {multiple && (
          <div
            className={classNames(
              styles.labelForMultiSelect,
              lean && styles.lean,
            )}
          >
            {(selected as ComboBoxItem<T>[]).length === 0 && (
              <div className={styles.noSelected}>Select one...</div>
            )}
            {(selected as ComboBoxItem<T>[]).map(
              (option, i) =>
                option.label +
                (i < (selected as ComboBoxItem<T>[]).length - 1 ? ", " : ""),
            )}
          </div>
        )}
        <ComboboxButton className={styles.arrowWrapper}>
          <div className={classNames(styles.arrow, lean && styles.lean)}>
            <ChevronDownIcon />
          </div>
        </ComboboxButton>
      </div>
      <ComboboxOptions
        transition
        className={styles.dropdown}
        anchor="bottom start"
      >
        {filteredOptions.map((option) => (
          <ComboboxOption
            key={option.id}
            className={`${styles.dropdownItem} ${lean && styles.lean}`}
            value={option}
          >
            <div>{option.label}</div>
            <Check className={styles.check} />
          </ComboboxOption>
        ))}
      </ComboboxOptions>
    </Combobox>
  );
};

export default ComboBox;
